'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CookiePolicySectionProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export default function CookiePolicySection({ isExpanded, onToggle }: CookiePolicySectionProps) {
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    functional: true,
    marketing: false
  });

  const cookieTypes = [
    {
      id: 'essential',
      name: 'Essential Cookies',
      description: 'Required for basic website functionality, security, and session management',
      required: true,
      examples: 'Authentication tokens, security settings, language preferences'
    },
    {
      id: 'functional',
      name: 'Functional Cookies',
      description: 'Enable enhanced features like chat support, booking preferences, and personalization',
      required: false,
      examples: 'Search history, apartment preferences, accessibility settings'
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      description: 'Track user behavior for advertising and analytics purposes',
      required: false,
      examples: 'Google Analytics, Facebook Pixel, advertising identifiers'
    }
  ];

  const handleToggle = (id: string) => {
    if (id !== 'essential') {
      setCookiePreferences(prev => ({
        ...prev,
        [id]: !prev[id as keyof typeof prev]
      }));
    }
  };

  return (
    <section id="cookie-policy" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Icon name="GlobeAltIcon" size={20} className="text-primary" />
              <span className="text-primary text-sm font-medium">Cookie Policy</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-text-primary mb-4">
              Cookie Usage & Consent
            </h2>
            <p className="text-lg text-text-secondary">
              We use cookies to improve your browsing experience and provide personalized content. You have full control over cookie preferences.
            </p>
          </div>

          <div className="space-y-6">
            {cookieTypes.map((cookie) => (
              <div
                key={cookie.id}
                className="bg-white rounded-xl shadow-card border border-border p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-text-primary">
                        {cookie.name}
                      </h3>
                      {cookie.required && (
                        <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded">
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-text-secondary mb-3">
                      {cookie.description}
                    </p>
                    <button
                      onClick={onToggle}
                      className="text-primary hover:text-primary/80 text-sm font-medium flex items-center space-x-1 transition-colors"
                    >
                      <span>{isExpanded ? 'Hide Examples' : 'Show Examples'}</span>
                      <Icon 
                        name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} 
                        size={14} 
                        className="text-current" 
                      />
                    </button>
                    {isExpanded && (
                      <div className="mt-3 p-3 bg-muted rounded-lg animate-scale-in">
                        <p className="text-text-secondary text-sm">
                          <span className="font-medium">Examples:</span> {cookie.examples}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => handleToggle(cookie.id)}
                      disabled={cookie.required}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        cookiePreferences[cookie.id as keyof typeof cookiePreferences]
                          ? 'bg-primary' :'bg-gray-300'
                      } ${cookie.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          cookiePreferences[cookie.id as keyof typeof cookiePreferences]
                            ? 'translate-x-6' :'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-white rounded-xl shadow-card border border-border">
            <h4 className="font-semibold text-text-primary mb-4 flex items-center space-x-2">
              <Icon name="Cog6ToothIcon" size={20} className="text-primary" />
              <span>Browser Cookie Settings</span>
            </h4>
            <p className="text-text-secondary text-sm mb-4">
              You can also manage cookies through your browser settings. Most browsers allow you to:
            </p>
            <ul className="space-y-2 text-text-secondary text-sm">
              <li className="flex items-start space-x-2">
                <Icon name="CheckIcon" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <span>Block all cookies or only third-party cookies</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="CheckIcon" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <span>Delete cookies when you close your browser</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="CheckIcon" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <span>Receive notifications when websites set or access cookies</span>
              </li>
            </ul>
            <p className="text-text-secondary text-sm mt-4">
              Note: Blocking essential cookies may affect website functionality and prevent you from using certain features.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}